/* eslint-disable indent */
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  //Checkbox,
  //FormControlLabel,
  Skeleton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import ChipRadioGroup from "../common/RadioChip/RadioChip";
import { StatementType, UserDetailsType } from "../../types/genericType";
import { createUser, editUser } from "../../services/userService";
import "./NewUserStyles.scss";
import { getRoleIdByRole } from "../../utils/utils";
import { forgotPassword } from "../../services/authService";
import AutoCompleteMultiSelect from "../../components/common/autocompleteSelect/AutoCompleteMultiSelect";

type NewUserProps = {
  setDrawerOpen: Function;
  accountOptions: any[];
  setOnTxnSuccess: Function;
  setSnackbarMsg: Function;
  getUsersData: Function;
  editFlow?: boolean;
  selectedUser: UserDetailsType;
  isActive?: boolean;
  handleDrawerClose: Function;
};

const NewUser = (props: NewUserProps) => {
  const {
    setDrawerOpen,
    accountOptions,
    setOnTxnSuccess,
    setSnackbarMsg,
    getUsersData,
    editFlow,
    selectedUser,
    isActive,
    handleDrawerClose,
  } = props;
  const [userType, setUserType] = useState<string>("Client");
  const [firstName, setFirstName] = useState<string>(
    editFlow ? selectedUser?.firstName : ""
  );
  const [lastName, setLastName] = useState<string>(
    editFlow ? selectedUser?.lastName : ""
  );
  const [email, setEmail] = useState<string>(
    editFlow ? selectedUser?.email : ""
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [accounts, setAccounts] = useState<string[]>(
    selectedUser.accounts || []
  );
  const [statements, setStatements] = useState<StatementType[]>(
    selectedUser.statements || []
  );
  const [isResetBtnLoading, setIsResetBtnLoading] = useState<boolean>(false);

  const handleCreateUser = () => {
    if (
      !firstName ||
      !lastName ||
      !email ||
      (userType === "Client" && !accounts.length) ||
      (userType === "Client" && !statements.length)
    ) {
      setError("Please fill all required fields");
      return;
    } else {
      setIsLoading(true);
      createUser(
        firstName,
        lastName,
        email,
        userType === "Client" ? accounts : null,
        userType === "Client" ? statements : null,
        getRoleIdByRole(userType)
      )
        .then(() => {
          handleDrawerClose();
          setOnTxnSuccess(true);
          getUsersData();
          setSnackbarMsg("User created successfully");
          setIsLoading(false);
          setDrawerOpen(false);
        })
        .catch(() => {
          setError("Error creating user");
          setOnTxnSuccess(false);
          setIsLoading(false);
        });
    }
  };

  const handleEditUser = () => {
    if (
      !firstName ||
      !lastName ||
      !email ||
      (userType === "Client" && !accounts.length) ||
      (userType === "Client" && !statements.length)
    ) {
      setError("Please fill all required fields");
      return;
    } else {
      setIsLoading(true);
      editUser(
        selectedUser?.id,
        firstName,
        lastName,
        email,
        userType === "Client" ? accounts : null,
        userType === "Client" ? statements : null,
        isActive ?? true
      )
        .then(() => {
          handleDrawerClose();
          setOnTxnSuccess(true);
          getUsersData();
          setSnackbarMsg("User details updated successfully");
          setIsLoading(false);
          setDrawerOpen(false);
        })
        .catch(() => {
          setError("Error updating user details");
          setOnTxnSuccess(false);
          setIsLoading(false);
        });
    }
  };

  // const handleAccountSelection = (
  //   accountId: string,
  //   type: keyof StatementType
  // ) => {
  //   const updatedStatements = statements.map((statement) =>
  //     statement.account === accountId
  //       ? {
  //           ...statement,
  //           [type]: !statement[type],
  //         }
  //       : statement
  //   );
  //   setStatements(updatedStatements);
  // };

  useEffect(() => {
    const updatedStatements = statements.filter((statement) =>
      accounts.includes(statement.account)
    );

    const newAccounts = accounts.filter(
      (account) =>
        !updatedStatements.some((statement) => statement.account === account)
    );

    if (newAccounts.length > 0) {
      newAccounts.forEach((account) => {
        updatedStatements.push({
          account: account,
          email: false,
          mail: false,
        });
      });
    }
    setStatements(updatedStatements);
  }, [accounts]);

  useEffect(() => {
    if (
      firstName ||
      lastName ||
      email ||
      accounts.length ||
      statements.length
    ) {
      setError("");
    }
  }, [firstName, lastName, email, accounts.length, statements.length]);

  useEffect(() => {
    if (editFlow) {
      if (selectedUser?.role === "Administrator") {
        setUserType("Admin");
      } else {
        setUserType(selectedUser?.role);
      }
    }
  }, [editFlow, selectedUser?.role]);

  // const getAccountLabel = (account: string) => {
  //   const selectedOption = accountOptions.find(
  //     (option) => option.value === account
  //   );
  //   return selectedOption ? [selectedOption.label, selectedOption.isActive] : "";
  // };

  const handleResetPassword = () => {
    setIsResetBtnLoading(true);
    forgotPassword(email)
      .then(() => {
        handleDrawerClose();
        setIsResetBtnLoading(false);
        setOnTxnSuccess(true);
        setSnackbarMsg('Password reset sent successfully');
        setDrawerOpen(false);
      })
      .catch(() => {
        setIsResetBtnLoading(false);
        setOnTxnSuccess(true);
        setSnackbarMsg("Something went wrong!! Please try again");
      });
  };

  return (
    <Box className="new-user">
      <Stack spacing={3} className="user-container">
        {editFlow ? (
          <>
            <Typography className="user-title">User Type</Typography>
            <Typography className="user-type">{userType}</Typography>
          </>
        ) : (
          <ChipRadioGroup
            label="User Type"
            value={userType}
            setValue={setUserType}
            options={["Client", "Admin", "Auditor"]}
          />
        )}
        <Stack direction="row" spacing={2}>
          <TextField
            id="outlined-helperText"
            label="First Name"
            size="small"
            value={firstName}
            onChange={(event) => setFirstName(event.target.value as string)}
            required
          />
          <TextField
            id="outlined-helperText"
            label="Last Name"
            size="small"
            value={lastName}
            onChange={(event) => setLastName(event.target.value as string)}
            required
          />
        </Stack>
        <TextField
          id="outlined-error-helper-text"
          type="email"
          inputProps={{ "data-testid": "emailId" }}
          onChange={(event) => setEmail(event.target.value as string)}
          value={email}
          autoComplete="off"
          label="Email"
          variant="outlined"
          fullWidth
          size="small"
          required
        />
        {userType === "Client" &&
          (accountOptions.length > 0 ? (
            <AutoCompleteMultiSelect
              items={accountOptions}
              onSelectItem={setAccounts}
              selected={accounts}
              placeholder="Select an account"
              label="Select Account(s)"
              required
            />
          ) : (
            <Skeleton variant="rectangular" height={40} />
          ))}
        {/* {userType === "Client" && accounts.length > 0 && (
          <Stack direction="row" spacing={2} className="statements-header">
            <Typography variant="subtitle1" className="subtitle">
              Send statements to:{" "}
            </Typography>
            <Typography variant="subtitle1" className="subtitle1">
              Email
            </Typography>
            <Typography variant="subtitle1" className="subtitle1">
              Mail
            </Typography>
          </Stack>
        )} */}
        {/* {userType === "Client" &&
          statements.map((statement) => (
            <Stack
              direction="row"
              spacing={2}
              key={statement.account}
              className="statements-data"
            >
              <Typography className={getAccountLabel(statement.account)[1] ? "subtitle" : "subtitle disabled"}>
                {getAccountLabel(statement.account)[0]}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={statement.email}
                    onChange={() =>
                      handleAccountSelection(statement.account, "email")
                    }
                  />
                }
                label=""
                className="fs24"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={statement.mail}
                    onChange={() =>
                      handleAccountSelection(statement.account, "mail")
                    }
                  />
                }
                label=""
                className="mail"
              />
            </Stack>
          ))} */}
        {error && (
          <Typography variant="sFormHelper" className="error-text">
            {error}
          </Typography>
        )}
        <Stack direction="row" spacing={2}>
          <LoadingButton
            variant="contained"
            data-testid="createTxnBtn"
            size="small"
            className="create-button"
            onClick={editFlow ? handleEditUser : handleCreateUser}
            loading={isLoading}
          >
            {editFlow ? "SAVE CHANGES" : "Create User"}
          </LoadingButton>
          <Button className="cancel-button" onClick={() => handleDrawerClose()}>
            CANCEL
          </Button>
        </Stack>
        {editFlow && (
          <LoadingButton
            variant="contained"
            data-testid="createTxnBtn"
            size="small"
            className="reset-button"
            onClick={handleResetPassword}
            loading={isResetBtnLoading}
          >
            {"Reset Password"}
          </LoadingButton>
        )}
      </Stack>
    </Box>
  );
};

export default NewUser;
